import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  MessageCircle,
  Phone,
  Mail,
  FileText,
  HelpCircle,
  Send,
  CheckCircle2,
  Clock,
  Zap,
  Crown,
  BookOpen,
  ExternalLink,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

// WhatsApp number
const WHATSAPP_NUMBER = "56942940982";

export default function SupportPage({ user, onLogout }) {
  const [contactForm, setContactForm] = useState({
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Get user plan (default to 'free' if not set)
  // Normalize enterprise_annual to enterprise for feature access
  const rawPlan = user?.subscription_plan || user?.plan || "free";
  const userPlan = rawPlan === "enterprise_annual" ? "enterprise" : rawPlan;

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    
    if (!contactForm.subject || !contactForm.message) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API_BASE}/contact/support`, {
        name: user?.name || user?.email?.split("@")[0] || "Usuario",
        email: user?.email,
        subject: contactForm.subject,
        message: contactForm.message,
        plan: userPlan,
      }, getAuthHeaders());
      
      setSent(true);
      toast.success("Mensaje enviado correctamente");
      setContactForm({ subject: "", message: "" });
    } catch (error) {
      toast.error("Error al enviar mensaje. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const openWhatsApp = (priority = false) => {
    const baseMessage = priority 
      ? `Hola, soy usuario ENTERPRISE de AVX RF PRO. Necesito soporte prioritario.`
      : `Hola, soy usuario PRO de AVX RF PRO. Necesito ayuda con:`;
    
    const encodedMessage = encodeURIComponent(baseMessage);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, "_blank");
  };

  const openPhoneCall = () => {
    window.open(`tel:+${WHATSAPP_NUMBER}`, "_self");
  };

  const faqItems = [
    {
      question: "Como asigno frecuencias para mi equipo?",
      answer: "Ve a Frecuencias, selecciona tu marca de equipo, ajusta la cantidad de sistemas y haz clic en Calcular."
    },
    {
      question: "Como conecto mi analizador RF?",
      answer: "Ve a Hardware RF, conecta tu dispositivo USB y haz clic en Conectar. Usa Chrome o Edge."
    },
    {
      question: "Como exporto mis configuraciones?",
      answer: "En Mis Venues puedes exportar configuraciones como JSON para respaldo o compartir."
    },
    {
      question: "Que significa cada color de estado?",
      answer: "Verde = estable, Amarillo = riesgo menor, Rojo = interferencia detectada."
    },
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Centro de Soporte</h1>
          <p className="text-gray-400">
            Obtén ayuda según tu plan de suscripción
          </p>
        </div>

        {/* Plan Badge */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#111] border border-white/10 rounded-lg">
            <span className="text-gray-400 text-sm">Tu plan:</span>
            <Badge className={`${
              userPlan === "enterprise" ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/50" :
              userPlan === "pro" ? "bg-green-500/20 text-green-400 border-green-500/50" :
              "bg-gray-500/20 text-gray-400 border-gray-500/50"
            }`}>
              {userPlan === "enterprise" ? (
                <><Crown className="w-3 h-3 mr-1" /> {rawPlan === "enterprise_annual" ? "Enterprise Anual" : "Enterprise"}</>
              ) : userPlan === "pro" ? (
                <><Zap className="w-3 h-3 mr-1" /> Pro</>
              ) : (
                "Free"
              )}
            </Badge>
          </div>
        </div>

        {/* Support Options Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          
          {/* WhatsApp - Pro & Enterprise */}
          <Card className={`border transition-all ${
            userPlan === "free" 
              ? "bg-[#111] border-white/5 opacity-60" 
              : "bg-[#111] border-green-500/30 hover:border-green-500/50"
          }`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-green-400" />
                </div>
                {userPlan === "free" && (
                  <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-400">
                    PRO+
                  </Badge>
                )}
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">WhatsApp</h3>
              <p className="text-sm text-gray-400 mb-4">
                {userPlan === "free" 
                  ? "Disponible en planes Pro y Enterprise"
                  : "Chat directo con soporte técnico"
                }
              </p>
              {userPlan !== "free" ? (
                <Button 
                  onClick={() => openWhatsApp(userPlan === "enterprise")}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="whatsapp-button"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Abrir WhatsApp
                </Button>
              ) : (
                <Button 
                  disabled
                  className="w-full bg-gray-700 cursor-not-allowed"
                >
                  No disponible
                </Button>
              )}
              {userPlan !== "free" && (
                <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Respuesta en menos de 2 horas
                </p>
              )}
            </CardContent>
          </Card>

          {/* Phone - Enterprise Only */}
          <Card className={`border transition-all ${
            userPlan === "enterprise" 
              ? "bg-[#111] border-yellow-500/30 hover:border-yellow-500/50" 
              : "bg-[#111] border-white/5 opacity-60"
          }`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                  <Phone className="w-6 h-6 text-yellow-400" />
                </div>
                {userPlan !== "enterprise" && (
                  <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-400">
                    ENTERPRISE
                  </Badge>
                )}
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Llamada Directa</h3>
              <p className="text-sm text-gray-400 mb-4">
                {userPlan === "enterprise" 
                  ? "Soporte telefónico prioritario"
                  : "Exclusivo para plan Enterprise"
                }
              </p>
              {userPlan === "enterprise" ? (
                <Button 
                  onClick={openPhoneCall}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 text-black"
                  data-testid="call-button"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Llamar Ahora
                </Button>
              ) : (
                <Button 
                  disabled
                  className="w-full bg-gray-700 cursor-not-allowed"
                >
                  No disponible
                </Button>
              )}
              {userPlan === "enterprise" && (
                <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  Atención inmediata
                </p>
              )}
            </CardContent>
          </Card>

          {/* Manual/FAQ - All Plans */}
          <Card className="bg-[#111] border-blue-500/30 hover:border-blue-500/50 transition-all">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-blue-400" />
                </div>
                <Badge variant="outline" className="text-xs border-green-500/50 text-green-400">
                  TODOS
                </Badge>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Manual de Usuario</h3>
              <p className="text-sm text-gray-400 mb-4">
                Guía completa con todas las funcionalidades
              </p>
              <Button 
                onClick={() => window.open("/manual", "_blank")}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="manual-button"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Ver Manual
              </Button>
              <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                <FileText className="w-3 h-3" />
                Actualizado constantemente
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Contact Form - For Free Plan */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Contact Form */}
          <Card className="bg-[#111] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Formulario de Contacto</h3>
                  <p className="text-xs text-gray-500">
                    {userPlan === "free" ? "Respuesta en 24-48 horas" : "Respuesta en menos de 12 horas"}
                  </p>
                </div>
              </div>

              {sent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">Mensaje Enviado</h4>
                  <p className="text-gray-400 text-sm mb-4">
                    Te responderemos pronto a tu email
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => setSent(false)}
                    className="border-white/20"
                  >
                    Enviar otro mensaje
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmitContact} className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Asunto</label>
                    <Input
                      placeholder="Ej: Problema con asignación de frecuencias"
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                      className="bg-black/50 border-white/10"
                      data-testid="contact-subject"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Mensaje</label>
                    <Textarea
                      placeholder="Describe tu problema o consulta..."
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="bg-black/50 border-white/10 min-h-[120px]"
                      data-testid="contact-message"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={sending}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    data-testid="contact-submit"
                  >
                    {sending ? (
                      <>Enviando...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Enviar Mensaje</>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* FAQ */}
          <Card className="bg-[#111] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Preguntas Frecuentes</h3>
                  <p className="text-xs text-gray-500">Respuestas rápidas</p>
                </div>
              </div>

              <div className="space-y-3">
                {faqItems.map((item) => (
                  <div 
                    key={item.question}
                    className="p-3 bg-black/30 rounded-lg border border-white/5 hover:border-cyan-500/30 transition cursor-pointer"
                    onClick={() => toast.info(item.answer, { duration: 5000 })}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-white">{item.question}</p>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                variant="ghost" 
                className="w-full mt-4 text-cyan-400 hover:text-cyan-300"
                onClick={() => window.open("/manual#faq", "_blank")}
              >
                Ver todas las preguntas
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Upgrade CTA for Free users */}
        {userPlan === "free" && (
          <Card className="mt-6 bg-gradient-to-r from-green-900/20 to-yellow-900/20 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Obtén soporte prioritario</h3>
                    <p className="text-sm text-gray-400">
                      Actualiza a Pro o Enterprise para WhatsApp y llamadas directas
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => window.location.href = "/subscription"}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Ver Planes
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
